import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { showToast } from '@utils';
import {
    apiTradeIndividualOrder,
    apiUpdateOrder,
    apiRemoveOrder
} from '@store';
import { get } from 'lodash';

const STATUS_OPTIONS = ['Firm'];
const EXPIRY_OPTIONS = ['Good Till Cancelled'];

// const MOCK_TRADING_ACCOUNTS
//     '',
//     'Simulated Trading Account 1',
//     'Simulated Trading Account 2'
// ];

const getNumericDisplayValue = (value, digits = 3) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '';

    const rounded =
        Math.round(numericValue * Math.pow(10, digits)) / Math.pow(10, digits);
    return rounded.toFixed(digits);
};

const getTradeDefaults = (tradeEntry) => {
    const productData = tradeEntry?.row?.[tradeEntry?.productName] || {};
    const side = tradeEntry?.side || 'bid';

    // support multiple incoming shapes: normalized {Bid,Ask},
    // single order entry with `Price`/`Volume`, or {bids:[], asks:[]} arrays
    const getProductSideOrder = (pd, s) => {
        if (!pd) return null;
        // normalized top-of-book
        if (s === 'ask' && pd.Ask != null)
            return {
                Price: pd.Ask,
                Volume: pd.AskQty ?? pd.Quantity ?? pd.Qty
            };
        if (s === 'bid' && pd.Bid != null)
            return {
                Price: pd.Bid,
                Volume: pd.BidQty ?? pd.Quantity ?? pd.Qty
            };
        // object representing a single order
        if (pd.Price != null)
            return {
                Price: pd.Price,
                Volume: pd.Volume ?? pd.Quantity ?? pd.Qty
            };
        // array-shaped
        if (s === 'ask' && Array.isArray(pd.asks) && pd.asks.length > 0)
            return pd.asks[0];
        if (s === 'bid' && Array.isArray(pd.bids) && pd.bids.length > 0)
            return pd.bids[0];
        return null;
    };

    const sideOrder = getProductSideOrder(productData, side);
    const rawPrice = sideOrder?.Price ?? null;
    const rawQuantity =
        sideOrder?.Volume ?? sideOrder?.Quantity ?? sideOrder?.Qty ?? null;

    const roundedPrice =
        rawPrice != null ? Math.round(rawPrice * 1000) / 1000 : null;

    return {
        price: roundedPrice != null ? String(roundedPrice) : '',
        quantity: getNumericDisplayValue(rawQuantity),
        allOrNone: !!productData.AllOrNone,
        // status: productData.Status || 'Firm',
        status: 'Firm',
        // expiry: productData.ExpiryTime || 'Good Till Cancelled',
        expiry: 'Good Till Cancelled',
        tradingAccount: productData.TradingAccount || '',
        confirmDetails: false
    };
};

const getMaxDealQuantity = (tradeEntry) => {
    const productData = tradeEntry?.row?.[tradeEntry?.productName] || {};
    const side = tradeEntry?.side || 'bid';

    const getProductSideOrder = (pd, s) => {
        if (!pd) return null;
        if (s === 'ask' && pd.AskQty != null) return pd.AskQty;
        if (s === 'bid' && pd.BidQty != null) return pd.BidQty;
        if (pd.Price != null) return pd.Volume ?? pd.Quantity ?? pd.Qty ?? null;
        if (s === 'ask' && Array.isArray(pd.asks) && pd.asks.length > 0)
            return pd.asks[0].Volume ?? pd.asks[0].Quantity ?? pd.asks[0].Qty;
        if (s === 'bid' && Array.isArray(pd.bids) && pd.bids.length > 0)
            return pd.bids[0].Volume ?? pd.bids[0].Quantity ?? pd.bids[0].Qty;
        return null;
    };

    const rawQuantity = getProductSideOrder(productData, side);
    const numericQuantity = Number(rawQuantity);

    if (!Number.isFinite(numericQuantity)) {
        return null;
    }

    return numericQuantity;
};

const createTradePayload = (tradeEntry, values) => {
    const productData = tradeEntry?.row?.[tradeEntry?.productName] || {};
    const side = tradeEntry?.side || 'bid';

    // For Deal actions, use the raw OrderId from the order object (as stored in data)
    const orderId =
        side === 'bid'
            ? productData._rawBid?.OrderId || productData.BidRawOrderId || ''
            : productData._rawAsk?.OrderId || productData.AskRawOrderId || '';

    return {
        engine_id: String(productData.EngineId || '1'),
        order_id: String(orderId),
        broker_id: Number(productData.BrokerId || 1),
        volume: Number(values.quantity),
        inst_name: productData.InstName || tradeEntry?.productName || 'THE EEX',
        first_sequence_id: Number(productData.FirstSequenceId || 0),
        first_sequence_item_name: tradeEntry?.row?.Contract || ''
    };
};

// Builds the update payload: spreads all existing productData, overrides the
// relevant side fields (Bid/Ask, BidQty/AskQty, AllOrNone), and also includes
// the backend-required snake_case fields.
// NOTE: old_broker_id (OldBrokerID) is required by the EEX engine for update
// and delete actions (engine error code 373).
const createUpdatePayload = (tradeEntry, values, action) => {
    const productData = tradeEntry?.row?.[tradeEntry?.productName] || {};
    const side = action === 'Bid' ? 'BID' : 'ASK';

    // Resolve the actual engine OrderId from the raw order object.
    // productData.OrderId is set to PersistentOrderId for display — do NOT use it here.
    const rawOrderId =
        action === 'Bid'
            ? productData._rawBid?.OrderId || productData.BidRawOrderId || ''
            : productData._rawAsk?.OrderId || productData.AskRawOrderId || '';

    const updatedSideFields =
        action === 'Bid'
            ? { Bid: Number(values.price), BidQty: Number(values.quantity) }
            : { Ask: Number(values.price), AskQty: Number(values.quantity) };

    return {
        // Full existing productData (context + order identification)
        ...productData,
        // Override the side's price/qty fields with new values
        ...updatedSideFields,
        // Explicitly set the correct engine OrderId (overrides the display-only
        // productData.OrderId which holds PersistentOrderId, not the engine ID)
        OrderId: String(rawOrderId),
        order_id: String(rawOrderId),
        AllOrNone: Boolean(values.allOrNone),
        contract: tradeEntry?.row?.Contract || '',
        // Backend-required fields (same format as insert_order)
        engine_id: String(productData.EngineId || '1'),
        price: Number(values.price),
        volume: Number(values.quantity),
        side,
        inst_name: productData.InstName || tradeEntry?.productName || 'THE EEX',
        first_sequence_id: Number(productData.FirstSequenceId || 0),
        first_sequence_item_name: tradeEntry?.row?.Contract || '',
        all_or_none: Boolean(values.allOrNone),
        broker_id: Number(productData.BrokerId || 1),
        // Required by EEX engine for update/delete (error code 373 without it)
        old_broker_id: Number(
            productData.OldBrokerId ?? productData.BrokerId ?? 1
        ),
        OldBrokerID: Number(
            productData.OldBrokerId ?? productData.BrokerId ?? 1
        ),
        route_id: Number(productData.RouteId || 1),
        old_route_id: Number(productData.OldRouteId ?? productData.RouteId ?? 1)
    };
};

const isValidPositiveQuantity = (quantity) => {
    if (quantity === '' || quantity === null || quantity === undefined) {
        return false;
    }

    const numericQuantity = Number(quantity);
    return Number.isFinite(numericQuantity) && numericQuantity > 0;
};

function AddBidForm({
    tradeEntry,
    onSubmit,
    onRemove,
    loading,
    allOrNone,
    onAllOrNoneChange
}) {
    const defaults = useMemo(() => {
        const d = getTradeDefaults(tradeEntry);
        return {
            price: d.price,
            quantity: d.quantity,
            status: d.status,
            expiry: d.expiry
        };
    }, [tradeEntry]);
    const [values, setValues] = useState(defaults);

    useEffect(() => {
        setValues(defaults);
    }, [defaults]);

    const handleChange = (field) => (event) => {
        if (field === 'allOrNone') {
            onAllOrNoneChange(event.target.checked);
            return;
        }
        setValues((current) => ({ ...current, [field]: event.target.value }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        onSubmit(
            createUpdatePayload(tradeEntry, { ...values, allOrNone }, 'Bid')
        );
    };

    const handleRemove = (event) => {
        event.preventDefault();
        onRemove(
            createUpdatePayload(tradeEntry, { ...values, allOrNone }, 'Bid')
        );
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        bgcolor: '#eaf3fb',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#0f5b94'
                        }}
                    >
                        {tradeEntry?.productName || 'EEX'}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 20,
                            fontWeight: 600,
                            color: '#0f5b94'
                        }}
                    >
                        {tradeEntry?.row?.Contract || '—'}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2}>
                    <TextField
                        label="Price"
                        type="number"
                        fullWidth
                        size="small"
                        value={values.price}
                        onChange={handleChange('price')}
                        inputProps={{ step: 'any' }}
                    />
                    <TextField
                        label="Quantity"
                        type="number"
                        fullWidth
                        size="small"
                        value={values.quantity}
                        onChange={handleChange('quantity')}
                        inputProps={{ step: 'any', min: 0 }}
                        disabled={values.allOrNone}
                        helperText={
                            values.allOrNone
                                ? 'Quantity is locked when AoN is enabled.'
                                : ''
                        }
                    />
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={allOrNone}
                                onChange={handleChange('allOrNone')}
                            />
                        }
                        label="AoN"
                    />
                </Stack>

                <Stack direction="row" spacing={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="add-bid-status-label">
                            Status
                        </InputLabel>
                        <Select
                            labelId="add-bid-status-label"
                            label="Status"
                            value={values.status}
                            onChange={handleChange('status')}
                        >
                            {STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel id="add-bid-expiry-label">
                            Expiry
                        </InputLabel>
                        <Select
                            labelId="add-bid-expiry-label"
                            label="Expiry"
                            value={values.expiry}
                            onChange={handleChange('expiry')}
                        >
                            {EXPIRY_OPTIONS.map((e) => (
                                <MenuItem key={e} value={e}>
                                    {e}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                >
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            loading ||
                            values.price === '' ||
                            values.quantity === ''
                        }
                        sx={{
                            minWidth: 140,
                            bgcolor: '#29a000',
                            '&:hover': { bgcolor: '#228400' }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: 'white' }}
                            />
                        ) : (
                            'Update Bid'
                        )}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={loading}
                        onClick={handleRemove}
                        sx={{
                            minWidth: 140,
                            bgcolor: '#c62828',
                            '&:hover': { bgcolor: '#a31515' }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: 'white' }}
                            />
                        ) : (
                            'Remove Bid'
                        )}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

function AddAskForm({
    tradeEntry,
    onSubmit,
    onRemove,
    loading,
    allOrNone,
    onAllOrNoneChange
}) {
    const defaults = useMemo(() => {
        const d = getTradeDefaults(tradeEntry);
        return {
            price: d.price,
            quantity: d.quantity,
            status: d.status,
            expiry: d.expiry
        };
    }, [tradeEntry]);
    const [values, setValues] = useState(defaults);

    useEffect(() => {
        setValues(defaults);
    }, [defaults]);

    const handleChange = (field) => (event) => {
        if (field === 'allOrNone') {
            onAllOrNoneChange(event.target.checked);
            return;
        }
        setValues((current) => ({ ...current, [field]: event.target.value }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        onSubmit(
            createUpdatePayload(tradeEntry, { ...values, allOrNone }, 'Ask')
        );
    };

    const handleRemove = (event) => {
        event.preventDefault();
        onRemove(
            createUpdatePayload(tradeEntry, { ...values, allOrNone }, 'Ask')
        );
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        bgcolor: '#eaf3fb',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#0f5b94'
                        }}
                    >
                        {tradeEntry?.productName || 'EEX'}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 20,
                            fontWeight: 600,
                            color: '#0f5b94'
                        }}
                    >
                        {tradeEntry?.row?.Contract || '—'}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2}>
                    <TextField
                        label="Price"
                        type="number"
                        fullWidth
                        size="small"
                        value={values.price}
                        onChange={handleChange('price')}
                        inputProps={{ step: 'any' }}
                    />
                    <TextField
                        label="Quantity"
                        type="number"
                        fullWidth
                        size="small"
                        value={values.quantity}
                        onChange={handleChange('quantity')}
                        inputProps={{ step: 'any', min: 0 }}
                        disabled={allOrNone}
                        helperText={
                            allOrNone
                                ? 'Quantity is locked when AoN is enabled.'
                                : ''
                        }
                    />
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={allOrNone}
                                onChange={handleChange('allOrNone')}
                            />
                        }
                        label="AoN"
                    />
                </Stack>

                <Stack direction="row" spacing={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="add-ask-status-label">
                            Status
                        </InputLabel>
                        <Select
                            labelId="add-ask-status-label"
                            label="Status"
                            value={values.status}
                            onChange={handleChange('status')}
                        >
                            {STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel id="add-ask-expiry-label">
                            Expiry
                        </InputLabel>
                        <Select
                            labelId="add-ask-expiry-label"
                            label="Expiry"
                            value={values.expiry}
                            onChange={handleChange('expiry')}
                        >
                            {EXPIRY_OPTIONS.map((e) => (
                                <MenuItem key={e} value={e}>
                                    {e}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                >
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            loading ||
                            values.price === '' ||
                            values.quantity === ''
                        }
                        sx={{
                            minWidth: 140,
                            bgcolor: '#ff6d00',
                            '&:hover': { bgcolor: '#d45500' }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: 'white' }}
                            />
                        ) : (
                            'Update Ask'
                        )}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={loading}
                        onClick={handleRemove}
                        sx={{
                            minWidth: 140,
                            bgcolor: '#c62828',
                            '&:hover': { bgcolor: '#a31515' }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: 'white' }}
                            />
                        ) : (
                            'Remove Ask'
                        )}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

function DealBidForm({ tradeEntry, onSubmit, loading, allOrNone }) {
    const defaults = useMemo(() => getTradeDefaults(tradeEntry), [tradeEntry]);
    const maxDealQuantity = useMemo(
        () => getMaxDealQuantity(tradeEntry),
        [tradeEntry]
    );
    const [values, setValues] = useState(defaults);
    const quantityIsValid = isValidPositiveQuantity(values.quantity);
    const quantityHasError = values.quantity !== '' && !quantityIsValid;
    // allOrNone comes from parent (shared across tabs) — do not read from values
    const allOrNoneLabel = allOrNone ? 'All or none: ON' : 'All or none: OFF';
    const allOrNoneColor = allOrNone ? '#c41a1a' : '#228400';

    useEffect(() => {
        setValues(defaults);
    }, [defaults]);

    const handleChange = (field) => (event) => {
        const nextValue =
            field === 'confirmDetails'
                ? event.target.checked
                : event.target.value;

        if (field === 'quantity' && !allOrNone && maxDealQuantity != null) {
            const numericValue = Number(nextValue);
            if (
                Number.isFinite(numericValue) &&
                numericValue > maxDealQuantity
            ) {
                setValues((current) => ({
                    ...current,
                    quantity: String(maxDealQuantity)
                }));
                return;
            }
        }

        setValues((current) => ({
            ...current,
            [field]: nextValue
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!values.confirmDetails) {
            return;
        }

        if (!quantityIsValid) {
            showToast('error', 'Quantity must be greater than 0.');
            return;
        }
        onSubmit(createTradePayload(tradeEntry, values));
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        bgcolor: '#e5edf4',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#0f5b94'
                        }}
                    >
                        {tradeEntry?.productName || 'EEX'}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 20,
                            fontWeight: 600,
                            color: '#0f5b94'
                        }}
                    >
                        {tradeEntry?.row?.Contract || '—'}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#0f5b94' }}>
                        Deal Bid
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        label="Qty"
                        type="number"
                        size="small"
                        fullWidth
                        value={values.quantity}
                        onChange={handleChange('quantity')}
                        inputProps={{
                            min: 0,
                            max: allOrNone
                                ? undefined
                                : (maxDealQuantity ?? undefined),
                            step: 'any'
                        }}
                        disabled={allOrNone}
                        error={quantityHasError}
                        helperText={
                            quantityHasError
                                ? 'Quantity must be greater than 0.'
                                : allOrNone
                                  ? 'Quantity is locked when AoN is enabled.'
                                  : ''
                        }
                    />
                    <TextField
                        label="Price"
                        type="number"
                        size="small"
                        fullWidth
                        value={values.price}
                        onChange={handleChange('price')}
                        inputProps={{ step: 'any' }}
                        disabled
                    />
                </Stack>

                {/* <FormControl fullWidth size="small">
                    <InputLabel id="trading-account-label">Trading account</InputLabel>
                    <Select
                        labelId="trading-account-label"
                        label="Trading account"
                        value={values.tradingAccount}
                        onChange={handleChange('tradingAccount')}
                        displayEmpty
                        renderValue={(selected) => selected || 'Trading account...'}
                    >
                        {MOCK_TRADING_ACCOUNTS.map((account) => (
                            <MenuItem
                                key={account || 'placeholder'}
                                value={account}
                                disabled={!account}
                            >
                                {account || 'Trading account...'}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl> */}

                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ color: allOrNoneColor, fontWeight: 600 }}>
                        {allOrNoneLabel}
                    </Typography>
                </Stack>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={values.confirmDetails}
                            onChange={handleChange('confirmDetails')}
                        />
                    }
                    label="I confirm these details are correct"
                />

                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                >
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            loading ||
                            !values.confirmDetails ||
                            values.price === '' ||
                            values.quantity === '' ||
                            quantityHasError
                        }
                        sx={{
                            minWidth: 140,
                            bgcolor: '#c41a1a',
                            '&:hover': { bgcolor: '#a01515' }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: 'white' }}
                            />
                        ) : (
                            'Sell'
                        )}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

function DealAskForm({ tradeEntry, onSubmit, loading, allOrNone }) {
    const defaults = useMemo(() => getTradeDefaults(tradeEntry), [tradeEntry]);
    const maxDealQuantity = useMemo(
        () => getMaxDealQuantity(tradeEntry),
        [tradeEntry]
    );
    const [values, setValues] = useState(defaults);
    const quantityIsValid = isValidPositiveQuantity(values.quantity);
    const quantityHasError = values.quantity !== '' && !quantityIsValid;
    // allOrNone comes from parent (shared across tabs) — do not read from values
    const allOrNoneLabel = allOrNone ? 'All or none: ON' : 'All or none: OFF';
    const allOrNoneColor = allOrNone ? '#c41a1a' : '#228400';

    useEffect(() => {
        setValues(defaults);
    }, [defaults]);

    const handleChange = (field) => (event) => {
        const nextValue =
            field === 'confirmDetails'
                ? event.target.checked
                : event.target.value;

        if (field === 'quantity' && !allOrNone && maxDealQuantity != null) {
            const numericValue = Number(nextValue);
            if (
                Number.isFinite(numericValue) &&
                numericValue > maxDealQuantity
            ) {
                setValues((current) => ({
                    ...current,
                    quantity: String(maxDealQuantity)
                }));
                return;
            }
        }

        setValues((current) => ({
            ...current,
            [field]: nextValue
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!values.confirmDetails) {
            return;
        }

        if (!quantityIsValid) {
            showToast('error', 'Quantity must be greater than 0.');
            return;
        }

        onSubmit(createTradePayload(tradeEntry, values));
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        bgcolor: '#e5edf4',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#0f5b94'
                        }}
                    >
                        {tradeEntry?.productName || 'EEX'}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 20,
                            fontWeight: 600,
                            color: '#0f5b94'
                        }}
                    >
                        {tradeEntry?.row?.Contract || '—'}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#0f5b94' }}>
                        Deal Ask
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        label="Qty"
                        type="number"
                        size="small"
                        fullWidth
                        value={values.quantity}
                        onChange={handleChange('quantity')}
                        inputProps={{
                            min: 0,
                            max: allOrNone
                                ? undefined
                                : (maxDealQuantity ?? undefined),
                            step: 'any'
                        }}
                        disabled={allOrNone}
                        error={quantityHasError}
                        helperText={
                            quantityHasError
                                ? 'Quantity must be greater than 0.'
                                : allOrNone
                                  ? 'Quantity is locked when AoN is enabled.'
                                  : ''
                        }
                    />
                    <TextField
                        label="Price"
                        type="number"
                        size="small"
                        fullWidth
                        value={values.price}
                        onChange={handleChange('price')}
                        inputProps={{ step: 'any' }}
                        disabled
                    />
                </Stack>

                {/* <FormControl fullWidth size="small">
                    <InputLabel id="trading-account-label">
                        Trading account
                    </InputLabel>
                    <Select
                        labelId="trading-account-label"
                        label="Trading account"
                        value={values.tradingAccount}
                        onChange={handleChange('tradingAccount')}
                        displayEmpty
                        renderValue={(selected) =>
                            selected || 'Trading account...'
                        }
                    >
                        {MOCK_TRADING_ACCOUNTS.map((account) => (
                            <MenuItem
                                key={account || 'placeholder'}
                                value={account}
                                disabled={!account}
                            >
                                {account || 'Trading account...'}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl> */}

                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ color: allOrNoneColor, fontWeight: 600 }}>
                        {allOrNoneLabel}
                    </Typography>
                </Stack>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={values.confirmDetails}
                            onChange={handleChange('confirmDetails')}
                        />
                    }
                    label="I confirm these details are correct"
                />

                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                >
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            loading ||
                            !values.confirmDetails ||
                            values.price === '' ||
                            values.quantity === '' ||
                            quantityHasError
                        }
                        sx={{
                            minWidth: 140,
                            bgcolor: '#2f9a00',
                            '&:hover': { bgcolor: '#247900' }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: 'white' }}
                            />
                        ) : (
                            'Buy'
                        )}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

const findProductOrderByPersistentId = (
    rows,
    productName,
    contract,
    side,
    persistentOrderId
) => {
    if (
        !rows ||
        !Array.isArray(rows) ||
        !productName ||
        !contract ||
        !persistentOrderId
    ) {
        return null;
    }

    for (const row of rows) {
        if (row.Contract !== contract) continue;

        const productData = row[productName];
        if (!productData) continue;

        // Check the raw bid/ask references stored during normalization
        const rawOrder =
            side === 'bid' ? productData._rawBid : productData._rawAsk;
        if (
            rawOrder &&
            rawOrder.PersistentOrderId &&
            String(rawOrder.PersistentOrderId) === String(persistentOrderId)
        ) {
            return rawOrder;
        }

        // Fallback: check BidPersistentOrderId / AskPersistentOrderId
        const sidePersistentId =
            side === 'bid'
                ? productData.BidPersistentOrderId
                : productData.AskPersistentOrderId;
        if (
            sidePersistentId &&
            String(sidePersistentId) === String(persistentOrderId)
        ) {
            return productData;
        }
    }

    return null;
};

const getCurrentProductOrder = (rows, products, tradeEntry, side) => {
    if (!rows || !products || !tradeEntry?.row || !tradeEntry?.productName) {
        return null;
    }

    const contract = tradeEntry.row.Contract;
    const productName = tradeEntry.productName;
    const productData = tradeEntry.row[productName] || {};
    const targetPersistentOrderId =
        side === 'bid'
            ? productData.BidPersistentOrderId
            : productData.AskPersistentOrderId;

    if (targetPersistentOrderId) {
        const matchedOrder = findProductOrderByPersistentId(
            rows,
            productName,
            contract,
            side,
            targetPersistentOrderId
        );

        return matchedOrder;
    }

    // Prefer the exact depth row if available (either by reference or by matching key fields)
    const exactRow = rows.find((r) => {
        if (r === tradeEntry.row) return true;
        if (r.Contract !== contract) return false;
        const a = r[productName];
        const b = tradeEntry.row[productName];
        if (!a || !b) return false;

        // Match using prices, quantities and UpdatedAt to identify the same depth
        return (
            (a.Bid === b.Bid || (a.Bid == null && b.Bid == null)) &&
            (a.Ask === b.Ask || (a.Ask == null && b.Ask == null)) &&
            (a.BidQty === b.BidQty || (a.BidQty == null && b.BidQty == null)) &&
            (a.AskQty === b.AskQty || (a.AskQty == null && b.AskQty == null)) &&
            (a.UpdatedAt === b.UpdatedAt || (!a.UpdatedAt && !b.UpdatedAt))
        );
    });

    if (exactRow) {
        return exactRow[productName];
    }

    // Fallback: return the product data/top-of-book for the product and side
    for (const row of rows) {
        if (row.Contract !== contract) continue;
        const productData = row[productName];
        if (!productData) continue;
        return productData;
    }

    return null;
};

const getSidePrice = (productData, side) => {
    if (!productData) return null;
    // normalized shape
    if (side === 'ask' && productData.Ask != null) return productData.Ask;
    if (side === 'bid' && productData.Bid != null) return productData.Bid;
    // single order entry
    if (productData.Price != null) return productData.Price;
    // array-shaped top-of-book
    if (
        side === 'ask' &&
        Array.isArray(productData.asks) &&
        productData.asks.length > 0
    )
        return productData.asks[0].Price;
    if (
        side === 'bid' &&
        Array.isArray(productData.bids) &&
        productData.bids.length > 0
    )
        return productData.bids[0].Price;

    return null;
};

export default function EexTradeModal({
    open,
    initialTab = 0,
    tradeEntry,
    onClose,
    side = 'bid',
    rows = [],
    products = [],
    onOrderUpdated
}) {
    const dispatch = useDispatch();
    const isTradeIndividualOrderLoading = useSelector(
        (state) => state.eex.isTradeIndividualOrderLoading
    );
    const isInsertOrderLoading = useSelector(
        (state) => state.eex.isInsertOrderLoading
    );
    const isUpdateOrderLoading = useSelector(
        (state) => state.eex.isUpdateOrderLoading
    );
    const isRemoveOrderLoading = useSelector(
        (state) => state.eex.isRemoveOrderLoading
    );

    const [activeTab, setActiveTab] = useState(initialTab);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [priceChanged, setPriceChanged] = useState(false);
    const [dealSnapshot, setDealSnapshot] = useState(null);
    // Shared AllOrNone state — persists across tab switches within the same modal open
    const [sharedAllOrNone, setSharedAllOrNone] = useState(false);

    useEffect(() => {
        if (open) {
            const productData =
                tradeEntry?.row?.[tradeEntry?.productName] || {};
            const isOwnData = !!productData.IsOwnData;
            
            // Only allow activeTab to be 1 (Update) if isOwnData is true
            const validTab = (initialTab === 1 && !isOwnData) ? 0 : initialTab;
            
            setActiveTab(validTab);
            setPriceChanged(false);
            // Initialise AllOrNone from the product row when modal opens
            setSharedAllOrNone(!!productData.AllOrNone);
            // Memoize the order snapshot when the modal opens for Deal actions.
            const currentOrder = getCurrentProductOrder(
                rows,
                products,
                tradeEntry,
                side
            );
            setDealSnapshot(currentOrder || null);
        }
    }, [open, initialTab, tradeEntry, side]);

    const modalTitle = useMemo(() => {
        const productName = tradeEntry?.productName || 'EEX';
        const contractName = tradeEntry?.row?.Contract || 'Order';
        return `${productName} ${contractName}`;
    }, [tradeEntry]);

    const isOwnData = useMemo(() => {
        const productData =
            tradeEntry?.row?.[tradeEntry?.productName] || {};
        return !!productData.IsOwnData;
    }, [tradeEntry, side]);

    const handleRemove = async (payload) => {
        setIsSubmitting(true);
        try {
            await dispatch(apiRemoveOrder(payload)).unwrap();
            const actionLabel = payload.side === 'BID' ? 'Bid' : 'Ask';
            showToast('success', `${actionLabel} order removed successfully`);
            onClose?.();
        } catch (error) {
            console.error('[EEX] Remove order error:', error);
            const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to remove the EEX order';
            showToast('error', errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (payload) => {
        const currentOrder = getCurrentProductOrder(
            rows,
            products,
            tradeEntry,
            side
        );

        // For Deal (Deal tab open) compare memoized snapshot vs latest data for the selected side.
        if (activeTab === 0) {
            const snapshotPrice = getSidePrice(dealSnapshot, side);
            const currentPrice = getSidePrice(currentOrder, side);

            const toNumeric = (v) => (v == null ? null : Number(v)); // Convert value to numeric
            const snapshotPriceNumeric = toNumeric(snapshotPrice); // Convert snapshot price to numeric
            const currentPriceNumeric = toNumeric(currentPrice); // Convert current price to numeric
            const roundTo = (v, digits = 3) => {
                if (v == null) return null;
                const n = Number(v);
                if (!Number.isFinite(n)) return null;
                return Number(n.toFixed(digits));
            };

            const numericEqual = (a, b, digits = 3) => {
                const ra = roundTo(a, digits);
                const rb = roundTo(b, digits);
                if (ra == null && rb == null) return true;
                if (!Number.isFinite(ra) || !Number.isFinite(rb)) return false;
                return ra === rb;
            };

            if (!numericEqual(snapshotPriceNumeric, currentPriceNumeric, 3)) {
                setPriceChanged(true);
                showToast(
                    'error',
                    'Price has changed. Please refresh and try again.'
                );
                setTimeout(() => {
                    setPriceChanged(false);
                    onClose?.();
                }, 5000);
                return;
            }
        } else if (!currentOrder) {
            setPriceChanged(true);
            showToast(
                'error',
                'Price has changed. Please refresh and try again.'
            );
            setTimeout(() => {
                setPriceChanged(false);
                onClose?.();
            }, 5000);
            return;
        }

        setIsSubmitting(true);

        try {
            // Determine which API to call based on the active tab
            // Tab 0 = Deal (trade_individual_order), Tab 1 = Add Bid/Ask (update_order)
            if (activeTab === 0) {
                const result = await dispatch(
                    apiTradeIndividualOrder(payload)
                ).unwrap();

                // Inspect inner EEX engine reply items for errors
                const innerItems = result?.data?.execute_reply?.items || [];
                const innerErrors = innerItems.filter(
                    (item) => item?.reply?.code && item.reply.code !== 0
                );

                if (innerErrors.length > 0) {
                    const engineMsg = innerErrors
                        .map(
                            (e) =>
                                e.reply.reason ||
                                e.reply.message ||
                                `Error code ${e.reply.code}`
                        )
                        .join('; ');
                    showToast('error', engineMsg);
                    return; // Don't close on error
                } else {
                    showToast('success', 'Trade executed successfully');
                }
            } else {
                const result = await dispatch(apiUpdateOrder(payload)).unwrap();

                // Inspect inner EEX engine reply items for errors
                const innerItems = result?.data?.execute_reply?.items || [];
                const innerErrors = innerItems.filter(
                    (item) => item?.reply?.code && item.reply.code !== 0
                );

                if (innerErrors.length > 0) {
                    const engineMsg = innerErrors
                        .map(
                            (e) =>
                                e.reply.reason ||
                                e.reply.message ||
                                `Error code ${e.reply.code}`
                        )
                        .join('; ');
                    showToast('error', engineMsg);
                    return; // Don't close on error
                } else {
                    const actionLabel = payload.side === 'BID' ? 'Bid' : 'Ask';
                    showToast(
                        'success',
                        `${actionLabel} order updated successfully`
                    );

                    onOrderUpdated?.(tradeEntry, payload);
                }
            }
            onClose?.();
        } catch (error) {
            console.error('[EEX] Submit error:', error);
            const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to submit the EEX trade';
            showToast('error', errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const loading =
        isTradeIndividualOrderLoading ||
        isInsertOrderLoading ||
        isUpdateOrderLoading ||
        isRemoveOrderLoading;

    return (
        <Dialog
            open={open}
            onClose={isSubmitting ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden'
                }
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    px: 2,
                    pt: 2,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box>
                    <Typography
                        sx={{ fontWeight: 700, fontSize: 18, color: '#245372' }}
                    >
                        {modalTitle}
                    </Typography>
                    {/* <Typography variant="body2" color="text.secondary">
                        {side === 'bid'
                            ? 'Add Bid and Deal Bid are available in separate tabs.'
                            : 'Add Ask and Deal Ask are available in separate tabs.'}
                    </Typography> */}
                </Box>
                <IconButton
                    onClick={onClose}
                    disabled={isSubmitting}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ px: 2, pt: 1 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, nextTab) => setActiveTab(nextTab)}
                    sx={{
                        minHeight: 42,
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#0b9cfd'
                        }
                    }}
                >
                    <Tab
                        label={side === 'bid' ? 'Deal Bid' : 'Deal Ask'}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            minHeight: 42
                        }}
                    />
                    {isOwnData && (
                        <Tab
                            label={side === 'bid' ? 'Update Bid' : 'Update Ask'}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                minHeight: 42
                            }}
                        />
                    )}
                </Tabs>
            </Box>

            <DialogContent sx={{ px: 2, pb: 2, pt: 2 }}>
                {priceChanged && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Price has changed. This modal will close in 5 seconds.
                        Please try again.
                    </Alert>
                )}
                {activeTab === 0 ? (
                    side === 'bid' ? (
                        <DealBidForm
                            tradeEntry={tradeEntry}
                            onSubmit={handleSubmit}
                            loading={loading}
                            allOrNone={sharedAllOrNone}
                        />
                    ) : (
                        <DealAskForm
                            tradeEntry={tradeEntry}
                            onSubmit={handleSubmit}
                            loading={loading}
                            allOrNone={sharedAllOrNone}
                        />
                    )
                ) : isOwnData && side === 'bid' ? (
                    <AddBidForm
                        tradeEntry={tradeEntry}
                        onSubmit={handleSubmit}
                        onRemove={handleRemove}
                        loading={loading}
                        allOrNone={sharedAllOrNone}
                        onAllOrNoneChange={setSharedAllOrNone}
                    />
                ) : isOwnData && side === 'ask' ? (
                    <AddAskForm
                        tradeEntry={tradeEntry}
                        onSubmit={handleSubmit}
                        onRemove={handleRemove}
                        loading={loading}
                        allOrNone={sharedAllOrNone}
                        onAllOrNoneChange={setSharedAllOrNone}
                    />
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
